import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { 
  Detection, 
  PatternPhrase, 
  Embedding, 
  PaginatedResponse, 
  Page,
  BatchAddResult,
  BatchDeleteResult,
  RemoveEmbeddingsResponse,
  DetectionStatsResponse 
} from '../types/api';

const API_BASE_URL = (window as any)._env_?.API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5111';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

// ===== JWT tokens storage & helpers =====
type Tokens = { accessToken: string; refreshToken: string; accessTokenExpiresAt?: string };
const TOKEN_KEY = 'authTokens';

const tokenStore = {
  get(): Tokens | null {
    try { const raw = localStorage.getItem(TOKEN_KEY); return raw ? JSON.parse(raw) as Tokens : null; } catch { return null; }
  },
  set(tokens: Tokens | null) {
    try { if (!tokens) localStorage.removeItem(TOKEN_KEY); else localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens)); } catch {}
  }
};

function setAuthHeader(config: AxiosRequestConfig) {
  const tokens = tokenStore.get();
  if (tokens?.accessToken) {
    config.headers = config.headers ?? {};
    (config.headers as any)['Authorization'] = `Bearer ${tokens.accessToken}`;
  }
}

api.interceptors.request.use(
  (config) => { setAuthHeader(config); return config; },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let pendingQueue: Array<{ resolve: (value?: void | PromiseLike<void>) => void; reject: (reason?: any) => void }> = [];

async function refreshToken(): Promise<void> {
  if (isRefreshing) return new Promise<void>((resolve, reject) => pendingQueue.push({ resolve, reject }));
  isRefreshing = true;
  try {
    const tokens = tokenStore.get();
    if (!tokens?.refreshToken) throw new Error('No refresh token');
    const resp = await axios.post(`${API_BASE_URL}/Auth/refresh`, { refreshToken: tokens.refreshToken }, { timeout: 15000 });
    const newTokens: Tokens = {
      accessToken: resp.data.accessToken,
      refreshToken: resp.data.refreshToken,
      accessTokenExpiresAt: resp.data.accessTokenExpiresAt
    };
    tokenStore.set(newTokens);
    pendingQueue.forEach(p => p.resolve());
  } catch (e) {
    tokenStore.set(null);
    pendingQueue.forEach(p => p.reject(e));
    throw e;
  } finally {
    isRefreshing = false;
    pendingQueue = [];
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config as AxiosRequestConfig & { _retry?: boolean };
    if ((status === 401 || status === 403) && !original._retry) {
      original._retry = true;
      try {
        await refreshToken();
        setAuthHeader(original);
        return api.request(original);
      } catch (_) {
        tokenStore.set(null);
        try { if (typeof window !== 'undefined') window.location.assign('/login'); } catch {}
      }
    }
    return Promise.reject(error);
  }
);

// ===== Auth API =====
export const authApi = {
  login: async (username: string, password: string): Promise<void> => {
    const resp = await api.post('/Auth/login', { username, password });
    const tokens: Tokens = {
      accessToken: resp.data.accessToken,
      refreshToken: resp.data.refreshToken,
      accessTokenExpiresAt: resp.data.accessTokenExpiresAt
    };
    tokenStore.set(tokens);
  },
  register: async (username: string, password: string): Promise<void> => {
    await api.post('/Auth/register', { username, password });
  },
  logout: async (): Promise<void> => {
    const tokens = tokenStore.get();
    tokenStore.set(null);
    if (tokens?.refreshToken) {
      try {
        void api.post('/Auth/logout', { refreshToken: tokens.refreshToken }, { validateStatus: () => true }).catch(() => {});
      } catch {}
    }
  },
  getTokens: () => tokenStore.get()
};

// Detection API
export const detectionApi = {
  detect: async (file: File, apiKey?: string): Promise<Detection> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/Detection', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  },

  getFileUrl: async (fileName: string, bucketName?: string): Promise<string> => {
    const response = await api.get('/File', {
      params: {
        fileName,
        ...(bucketName ? { bucketName } : {}),
      },
      responseType: 'blob', // Скачиваем бинарный поток аудио
    });
    return URL.createObjectURL(response.data);
  },

  getStats: async (from?: string, to?: string): Promise<DetectionStatsResponse> => {
    const response = await api.get('/Statistic', {
      params: { from, to }
    });
    return response.data;
  },

  getDetections: async (page: number = 1, pageSize: number = 10, sortBy?: string, sortDir?: 'asc' | 'desc', sessionId?: string): Promise<PaginatedResponse<Detection>> => {
    const response = await api.get('/Detection', {
      params: { page, pageSize, sortBy, sortDir, sessionId }, 
    });
    return response.data;
  },
};

// Patterns API (Обновлен под контроллер C#)
export const patternsApi = {
  getPatterns: async (
    pageIndex: number = 0,
    pageSize: number = 10,
    sortBy: string = 'Id',
    sortOrder: 'Asc' | 'Desc' = 'Desc',
    search?: string,
    isInWhitelist?: boolean
  ): Promise<Page<PatternPhrase>> => {
    const response = await api.get<Page<PatternPhrase>>('/Pattern', {
      params: {
        PageIndex: pageIndex,
        PageSize: pageSize,
        'Filter.Search': search?.trim() || undefined,
        'Filter.IsInWhitelist': isInWhitelist,
        SortKeySelector: sortBy,
        SortOrder: sortOrder,
      },
    });
    return response.data;
  },

  getPatternById: async (id: string): Promise<PatternPhrase> => {
    const response = await api.get<PatternPhrase>(`/Pattern/${encodeURIComponent(id)}`);
    return response.data;
  },

  addPatterns: async (patterns: Array<{ phrase: string; isInWhitelist: boolean }>): Promise<BatchAddResult> => {
    const response = await api.post<BatchAddResult>('/Pattern', patterns, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  removePatterns: async (ids: string[]): Promise<BatchDeleteResult> => {
    const response = await api.delete<BatchDeleteResult>('/Pattern', {
      data: ids,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },
};

// Embeddings API
export const embeddingsApi = {
  getEmbeddings: async (page: number = 1, pageSize: number = 10, sortBy?: string, sortDir?: 'asc' | 'desc', query?: string): Promise<PaginatedResponse<Embedding>> => {
    const response = await api.get('/Embedding', {
      params: { page, pageSize, sortBy, sortDir, query },
    });
    return response.data;
  },

  getEmbeddingById: async (id: string): Promise<Embedding> => {
    const response = await api.get(`/Embedding/${encodeURIComponent(id)}`);
    return response.data;
  },

  addEmbedding: async (file: File, description?: string): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }
    
    await api.post('/Embedding', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  uploadZip: async (zipFile: File, onProgress?: (percent: number) => void, uploadId?: string, signal?: AbortSignal): Promise<{ uploadId: string } > => {
    const formData = new FormData();
    formData.append('file', zipFile);

    const response = await api.post(`/Embedding/zip${uploadId ? `?uploadId=${encodeURIComponent(uploadId)}` : ''}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      validateStatus: () => true,
      signal,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress?.(percent);
        }
      },
    });
    if (response.status !== 202) {
      throw new Error(`Upload failed with status ${response.status}`);
    }
    return response.data;
  },

  cancelZip: async (uploadId: string): Promise<void> => {
    await api.delete(`/Embedding/zip/${encodeURIComponent(uploadId)}`, {
      validateStatus: () => true,
    });
  },

  removeEmbeddings: async (embeddingIds: string[]): Promise<RemoveEmbeddingsResponse> => {
    const response = await api.delete('/Embedding', {
      data: embeddingIds,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  getFileUrlByFileName: async (fileName: string, bucketName?: string): Promise<string> => {
    const response = await api.get('/File', {
      params: { fileName, bucketName: bucketName },
      responseType: 'blob', // Скачиваем бинарный поток аудио
    });
    return URL.createObjectURL(response.data);
  },
  
  updateDescription: async (id: string, description?: string): Promise<void> => {
    await api.patch(`/Embedding/${encodeURIComponent(id)}`, JSON.stringify(description ?? null), {
      headers: { 'Content-Type': 'application/json' },
    });
  },
};

// Prediction API
export const predictionApi = {
  startPrediction: async (
    fromIso: string,
    toIso: string,
    duplicatesThreshold: number,
    similarity: number
  ): Promise<{ uploadId: string }> => {
    const response = await api.get('/Prediction', {
      params: {
        from: fromIso,
        to: toIso,
        duplicatesThreshold,
        similarity,
      },
      validateStatus: () => true,
    });
    if (response.status !== 202 && response.status !== 200) {
      throw new Error(`Prediction start failed with status ${response.status}`);
    }
    const data = response.data || {};
    const uploadId: string = data.uploadId || data.id || data.guid || data.UploadId || data.Id;
    if (!uploadId) {
      throw new Error('Server did not return uploadId');
    }
    return { uploadId };
  },
  searchDuplicates: async (from: string, to: string, duplicatesThreshold: number): Promise<Array<Array<Detection>>> => {
    const response = await api.get('/Prediction', {
      params: { from, to, duplicatesThreshold },
    });
    return response.data;
  },
};

export const getProgressHubUrl = (): string => {
  const base = API_BASE_URL;
  return `${base}/hubs/progress`;
};

export default api;